import NiceModal from '@ebay/nice-modal-react';

import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import AutoString from 'components/field/AutoString';
import Button from 'components/field/Button';
import Chips from 'components/field/Chips';
import CurrencyInput from 'components/field/CurrencyInput';
import EnumSelect from 'components/field/EnumSelect';
import ImageInput from 'components/field/ImageInput';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import useAccessControl from 'hooks/useAccessControl';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useLocale from 'hooks/useLocale';
import useRestriction, { SYSTEM_LIMITS } from 'hooks/useRestriction';

import BrandForm from 'pages/Settings/Brands/BrandForm';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE, ENUMS, PRODUCTS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

import { faEye } from '@fortawesome/pro-solid-svg-icons';

import { ProductBoxDimensions } from './ProductForm.styled';
import * as S from './ProductForm.styled';

const defaultColSpan = {
	md: 6
};

const ProductFormInfoTab = ({ setProduct }) => {
	const formMethods = useFormContext();
	const isAuthorized = useAccessControl();
	const axios = useAxiosPrivate();
	const { track } = useAppTracking();
	const user = useSelector((state) => state.user);
	const { setValue, watch, control } = formMethods;
	const { LanguageCode } = useLocale();

	const CategoryAutocompleteColumns = {
		trValue: [{ accessor: 'NameTr', width: 200 }],
		engValue: [{ accessor: 'NameEn', width: 200 }],
		arabicValue: [{ accessor: 'NameAr', width: 200 }]
	};

	const { fields, append, remove } = useFieldArray({
		name: 'products',
		control
	});

	const [searchParams] = useSearchParams();
	const mode = searchParams.get('mode');
	const viewMode = mode === 'view';
	const [bundleFeature, setBundleFeature] = useState(false);

	const defaultSystem = user.localization.unitOfMeasurement ?? '';
	const isRestricted = useRestriction();
	const defaultLengthUnit = defaultSystem === 'metricSystem' ? 'cm' : 'inch';
	const defaultWeightUnit = defaultSystem === 'metricSystem' ? 'kg' : 'lb';
	const defaultCubicUnit =
		defaultSystem === 'metricSystem'
			? {
					name: 'cm',
					sup: '3'
				}
			: {
					name: 'inch',
					sup: '3'
				};
	const brandAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const deleteRow = ({ index }) => {
		remove(index);
	};
	const currencyWatcher = watch('currency');

	const width = watch('width');
	const height = watch('height');
	const length = watch('length');

	const productAutocompleteColumns = useMemo(() => [{ accessor: 'name', width: 200 }], []);
	const isBundleProduct = watch('bundleProduct');
	const tableColumns = useMemo(() => {
		const columns = [
			{
				id: 'sku',
				header: 'sku',
				size: 150,
				renderCell: ({ row }) => (
					<AutoComplete
						autoCompleteName={AUTOCOMPLETE.PRODUCTS}
						name={`products[${row.index}].productID`}
						value={watch(`products[${row.index}].productID`)}
						setValue={(value) => {
							setValue(`products[${row.index}].productID`, value?.ID ?? null, {
								shouldDirty: true,
								shouldValidate: true
							});
							setValue(`products[${row.index}].sku`, value?.SKU ?? null, {
								shouldDirty: true,
								shouldValidate: true
							});
						}}
						columns={productAutocompleteColumns}
						selectionLabelAccessor={['name']}
						viewMode
					/>
				),
				editCell: ({ row }) => (
					<AutoComplete
						autoCompleteName={AUTOCOMPLETE.PRODUCTS}
						name={`products[${row.index}].productID`}
						value={watch(`products[${row.index}].productID`)}
						setValue={(value) => {
							setValue(`products[${row.index}].productID`, value?.ID ?? null, {
								shouldDirty: true,
								shouldValidate: true
							});
							setValue(`products[${row.index}].sku`, value?.SKU ?? null, {
								shouldDirty: true,
								shouldValidate: true
							});
						}}
						columns={productAutocompleteColumns}
						selectionLabelAccessor={['name']}
						disabled={mode === 'view'}
					/>
				)
			},
			{
				id: 'quantity',
				header: 'quantity',
				required: true,
				renderCell: ({ row }) => <StringInput name={`products[${row.index}].quantity`} viewMode />,
				editCell: ({ row }) => (
					<StringInput name={`products[${row.index}].quantity`} disabled={mode === 'view'} />
				),
				size: 75
			}
		];

		if (mode !== 'view') {
			columns.push({
				id: 'editRow',
				size: 20
			});
			columns.push({
				id: 'deleteRow',
				size: 20
			});
		}
		return columns;
	}, []);

	useEffect(() => {
		if (!isAuthorized(['WMSAdvanceModule', 'IMSModule', 'WMSModule'])) return;
		axios
			.post(ADVANCE_WMS_MODULE.GET_CONFIGURE_OTOPACK)
			.then((response) => {
				if (response.status === 200) {
					if (response.data.activateBundleFeature) setBundleFeature(true);
					else setBundleFeature(false);
				} else {
					setBundleFeature(false);
				}
			})
			.catch((error) => {
				setBundleFeature(false);
				AppLogger.error(error);
			});
	}, []);

	const debouncedText = useDebounce(watch('hsCode'), 1000);

	const [hsCodes, setHsCodes] = useState([]);

	useEffect(() => {
		if (!debouncedText || debouncedText.length < 3) {
			setHsCodes([]);
			return;
		}
		axios
			.post(PRODUCTS.GET_HS_CODES, {
				keyword: debouncedText || ''
			})
			.then((response) => {
				if (response.status === 200) {
					setHsCodes(response.data);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
			});
	}, [debouncedText]);

	const navigate = useNavigate();

	return (
		<S.TabContainer style={{ display: 'flex', flexWrap: 'wrap' }}>
			<FormItem colSpan={defaultColSpan} label="productName" required>
				<StringInput name="name" disabled={viewMode} />
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="sku" required>
				<StringInput name="SKU" disabled={viewMode || !watch('isSkuEditable')} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="price" required>
				<CurrencyInput
					name="price"
					value={currencyWatcher}
					setValue={(value) => {
						setValue('currency', value, { shouldValidate: true, shouldDirty: true });
					}}
					disabled={viewMode}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="tax_amount">
				<CurrencyInput
					name="taxAmount"
					value={currencyWatcher}
					setValue={(value) => {
						setValue('currency', value, { shouldValidate: true, shouldDirty: true });
					}}
					disabled={viewMode}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="hsCode" toolTip="hsCode.tooltip">
				<AutoString
					name="ID"
					columns={[
						{ accessor: 'ID', width: 200 },
						{ accessor: 'Name', width: 200 }
					]}
					autoFilter={false}
					data={hsCodes}
					value={watch('hsCode')}
					setValue={(value) => {
						setValue('hsCode', value, { shouldValidate: true, shouldDirty: true });
					}}
					setSelectedOption={() => {}}
					disabled={viewMode}
					placeholder={'hsCodePlaceholder'}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="imageUrl">
				<ImageInput
					placeholder="selectFile"
					name="imageURL"
					value={watch('imageURL')}
					setValue={(value) => {
						setValue('imageURL', value, { shouldValidate: true, shouldDirty: true });
					}}
					disabled={viewMode}
					viewMode={viewMode}
				/>
			</FormItem>

			{!setProduct && (
				<>
					<FormItem colSpan={defaultColSpan} label="barcode">
						<StringInput name="barcode" disabled={viewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="category">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.PRODUCT_CATEGORIES}
							columns={CategoryAutocompleteColumns[LanguageCode]}
							selectionLabelAccessor={[CategoryAutocompleteColumns[LanguageCode][0].accessor]}
							value={watch('categoryId')}
							setValue={(value) => {
								setValue('categoryId', value?.ID ?? null, { shouldDirty: true, shouldValidate: true });
							}}
							disabled={viewMode}
							name="category"
						/>
					</FormItem>
					<FormItem
						colSpan={defaultColSpan}
						label="storeName"
						roleName="BrandingModule"
						required={user.userType === 'BrandManager'}>
						<AutoComplete
							name="brandID"
							disabled={viewMode}
							autoCompleteName={AUTOCOMPLETE.BRANDS}
							setValue={(value) => {
								setValue(`brandID`, value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							value={watch(`brandID`)}
							columns={brandAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							firstItem={
								isAuthorized('/autocomplete/brand/addANewBrand')
									? {
											action: () =>
												NiceModal.show(BrandForm, {
													setBrand: (value) => {
														setValue(`brandID`, value, {
															shouldValidate: true,
															shouldDirty: true
														});
													}
												}),
											label: <Typography color="var(--blue)">addANewBrand</Typography>,
											disabled: (dataSize) => isRestricted(SYSTEM_LIMITS.BRAND, dataSize),
											disabledTooltip: (
												<div style={{ display: 'block' }}>
													<ModuleToolTipContainer
														infoImage="lottie_multiBrand"
														title="multiBrandModule"
														description="multiBrandModuleSubtitle"
													/>

													<Button
														style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
														className="mt-3"
														label="showPlans"
														icon={faEye}
														variant="blue"
														onMouseDown={() => {
															track('moreThan3Brands');
															navigate(PATHS.OTO_MODULES);
														}}
													/>
												</div>
											)
										}
									: undefined
							}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="description">
						<StringInput disabled={viewMode} name="description" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="cubicMeter">
						<IntegerInput unit={defaultCubicUnit} disabled={viewMode} name="cubicMeter" />
					</FormItem>
					<FormItem colSpan={defaultColSpan}>
						<ProductBoxDimensions>
							<FormItem col={6} label="length">
								<IntegerInput
									unit={defaultLengthUnit}
									disabled={viewMode}
									min={0}
									value={watch('length')}
									setValue={(value) => {
										setValue('length', value ?? null, {
											shouldValidate: true,
											shouldDirty: true
										});
										setValue('cubicMeter', value * width * height);
									}}
								/>
							</FormItem>
							<FormItem col={6} label="width">
								<IntegerInput
									unit={defaultLengthUnit}
									disabled={viewMode}
									min={0}
									value={width}
									setValue={(value) => {
										setValue('width', value ?? null, {
											shouldValidate: true,
											shouldDirty: true
										});
										setValue('cubicMeter', value * length * height);
									}}
								/>
							</FormItem>
						</ProductBoxDimensions>
					</FormItem>

					<FormItem colSpan={defaultColSpan}>
						<ProductBoxDimensions>
							<FormItem col={6} label="height">
								<IntegerInput
									unit={defaultLengthUnit}
									disabled={viewMode}
									min={0}
									value={watch('height')}
									setValue={(value) => {
										setValue('height', value ?? null, {
											shouldValidate: true,
											shouldDirty: true
										});
										setValue('cubicMeter', width * length * value);
									}}
								/>
							</FormItem>
							<FormItem col={6} label="weight">
								<IntegerInput
									unit={defaultWeightUnit}
									disabled={viewMode}
									name="weight"
									min={0}
									allowFloat
								/>
							</FormItem>
						</ProductBoxDimensions>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="preorderOnly" roleName="OMSModule">
						<SwitchInput className="mt-3" formMode viewMode={viewMode} name="preorder" />
					</FormItem>

					{bundleFeature && (
						<FormItem
							colSpan={defaultColSpan}
							label="bundleProduct"
							roleName={['WMSAdvanceModule', 'WMSModule', 'IMSModule']}>
							<SwitchInput className="mt-3" formMode viewMode={viewMode} name="bundleProduct" />
						</FormItem>
					)}
					<FormItem colSpan={defaultColSpan} label="serialRequirement" roleName="PIMModule">
						<EnumSelect
							data={['Required', 'Not Applicable', 'Optional']}
							disabled={viewMode}
							viewMode={viewMode}
							setValue={(value) => {
								setValue('serialRequirement', value ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							value={watch('serialRequirement')}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="serialType" roleName="PIMModule">
						<EnumSelect
							enumName={ENUMS.PRODUCT_SERIAL_TYPE}
							disabled={viewMode}
							viewMode={viewMode}
							setValue={(value) => {
								setValue('serialType', value ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							value={watch('serialType')}
						/>
					</FormItem>

					<FormItem label="secondBarcode">
						<Chips
							name="secondBarcode"
							value={watch('secondBarcode')}
							setValue={(value) => {
								setValue(`secondBarcode`, value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={viewMode}
						/>
					</FormItem>

					{isBundleProduct && (
						<FormItem roleName={['WMSAdvanceModule', 'IMSModule']}>
							<Table
								columns={tableColumns}
								data={fields}
								onRowDelete={deleteRow}
								onRowAdd={() => {
									append({});
								}}
								showAddNewRowButton={mode !== 'view'}
								initEditMode={() => true}
							/>
						</FormItem>
					)}
				</>
			)}

			{user?.roles?.map?.((roleObj) => roleObj?.roleID)?.includes(114) && (
				<>
					<FormItem colSpan={defaultColSpan} label="sellOnOTOStore">
						<SwitchInput name="sellOnOTOStore" className="mt-3" formMode viewMode={viewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="sellingPrice">
						<CurrencyInput
							name="sellingPrice"
							value={currencyWatcher}
							setValue={(value) => {
								setValue('sellingPrice', value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={viewMode}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="priority">
						<IntegerInput name="priority" disabled={viewMode} />
					</FormItem>
				</>
			)}
		</S.TabContainer>
	);
};

export default ProductFormInfoTab;
